/**
 * DashboardLiveFeed — replaces the hardcoded `FEED` block on the
 * authenticated Hub dashboard (HD-96).
 *
 * Polls `GET /v1/dashboard/feed` every 30 s (tab-visibility gated),
 * renders skeleton on first load only, shows an empty state for
 * zero-data, an upgrade pill for Free plan, and an inline retry chip
 * after two consecutive failures.
 *
 * Tokens lifted from `routes/Dashboard.tsx` so the section feels native
 * without introducing new design tokens.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useDashboardFeed,
  type FeedItem,
  type FeedDeviceType,
} from '../lib/useDashboardFeed.js';
import { formatRelativeTime } from '../lib/formatRelativeTime.js';

// ─── Tokens (mirrors Dashboard.tsx) ────────────────────────────────────────

const T = {
  white: '#ffffff',
  navy: '#261F59',
  muted: '#E3EBF6',
  bgTint: '#F7F6FB',
  textMuted: '#6b7084',
  textFaint: '#9096a8',
  purple: '#351DBD',
  purpleLight: '#EDE9FC',
  blue: '#349DFF',
  blueLight: '#E5F2FF',
  orange: '#FF861D',
  orangeLight: '#FFF0E2',
  success: '#22c55e',
  shadow: {
    sm: '0 1px 3px rgba(53,29,189,0.04), 0 2px 8px rgba(53,29,189,0.04)',
  },
} as const;

const DEFAULT_LIMIT = 20;
const PREVIEW_COUNT = 5; // rows shown inline; the rest live in the "View all" modal
const QR_APP_HREF = '/qr';
const PRICING_HREF = '/account/billing?product=qr&action=buy';

// ─── Top-level component ───────────────────────────────────────────────────

export interface DashboardLiveFeedProps {
  /** Defaults to 20. Capped at 50 by the backend. */
  limit?: number;
}

export function DashboardLiveFeed({ limit = DEFAULT_LIMIT }: DashboardLiveFeedProps) {
  const { t } = useTranslation();
  const { data, state, error, consecutiveFailures, reload } = useDashboardFeed({ limit });

  // Re-render the relative timestamps every minute so "2 min ago" ticks
  // forward even between polls.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const [modalOpen, setModalOpen] = useState(false);

  const isInitialLoading = state === 'loading' && data === null;
  const isFree = data?.plan === 'free';
  const items: FeedItem[] = data?.items ?? [];
  const showRetry = consecutiveFailures >= 2;

  // "View all" opens the scrollable modal; only surface it when the inline
  // preview doesn't already show everything (and never on Free).
  const showViewAll = !isFree && items.length > PREVIEW_COUNT;

  // Close the modal on Escape, and drop it if the list shrinks under preview.
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen]);

  useEffect(() => {
    if (items.length <= PREVIEW_COUNT) setModalOpen(false);
  }, [items.length]);

  return (
    <section
      role="region"
      aria-label={t('feed.regionLabel')}
      style={{
        background: T.white,
        border: `1px solid ${T.muted}`,
        borderRadius: 20,
        padding: '20px 4px 8px',
        boxShadow: T.shadow.sm,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header
        eventsCount={isFree ? null : items.length}
        showViewAll={showViewAll}
        onViewAll={() => setModalOpen(true)}
      />

      {isInitialLoading && <SkeletonRows />}

      {!isInitialLoading && isFree && <FreeUpgradeEmpty />}

      {!isInitialLoading && !isFree && items.length === 0 && state === 'ready' && (
        <ZeroState />
      )}

      {!isInitialLoading && !isFree && items.length > 0 && (
        <RowsList items={items.slice(0, PREVIEW_COUNT)} now={now} />
      )}

      {showRetry && (
        <RetryChip
          onRetry={reload}
          errorMessage={error?.message ?? t('feed.couldNotRefresh')}
        />
      )}

      {modalOpen && (
        <FeedModal items={items} now={now} onClose={() => setModalOpen(false)} />
      )}
    </section>
  );
}

// ─── Header ────────────────────────────────────────────────────────────────

function Header({
  eventsCount,
  showViewAll,
  onViewAll,
}: {
  eventsCount: number | null;
  showViewAll: boolean;
  onViewAll: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        padding: '0 24px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${T.muted}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          aria-hidden="true"
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: T.success,
          }}
        />
        <h3 style={{ fontSize: 14, fontWeight: 700, color: T.navy, margin: 0 }}>
          {t('feed.title')}
        </h3>
        {eventsCount !== null && (
          <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 500 }}>
            · {eventsCount} {eventsCount === 1 ? t('feed.event') : t('feed.events')}
          </span>
        )}
      </div>
      {showViewAll && (
        <button
          type="button"
          onClick={onViewAll}
          style={{
            fontSize: 12,
            color: T.purple,
            fontWeight: 600,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            fontFamily: 'inherit',
          }}
        >
          {t('feed.viewAll')}
        </button>
      )}
    </div>
  );
}

// ─── "View all" modal ──────────────────────────────────────────────────────

/**
 * Fixed-size, scrollable modal listing the full feed. The box is always the
 * same size; only the body (row list) overflows and scrolls.
 */
function FeedModal({
  items,
  now,
  onClose,
}: {
  items: FeedItem[];
  now: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(38,31,89,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        zIndex: 1000,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('feed.title')}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 460,
          maxWidth: '92vw',
          height: 560,
          maxHeight: '85vh',
          background: T.white,
          borderRadius: 20,
          boxShadow: '0 24px 64px rgba(38,31,89,0.28)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header (fixed) */}
        <div
          style={{
            flexShrink: 0,
            padding: '18px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${T.muted}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              aria-hidden="true"
              style={{ width: 8, height: 8, borderRadius: '50%', background: T.success }}
            />
            <h3 style={{ fontSize: 15, fontWeight: 700, color: T.navy, margin: 0 }}>
              {t('feed.title')}
            </h3>
            <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 500 }}>
              · {items.length} {items.length === 1 ? t('feed.event') : t('feed.events')}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('feed.close')}
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              border: 'none',
              background: T.bgTint,
              color: T.textMuted,
              cursor: 'pointer',
              fontSize: 16,
              lineHeight: 1,
              display: 'grid',
              placeItems: 'center',
              fontFamily: 'inherit',
            }}
          >
            ×
          </button>
        </div>

        {/* Body (scrolls) */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px 12px' }}>
          {items.map((item) => (
            <Row key={item.id} item={item} now={now} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Rows ──────────────────────────────────────────────────────────────────

function RowsList({ items, now }: { items: FeedItem[]; now: number }) {
  return (
    <div style={{ padding: '6px 10px 12px', display: 'flex', flexDirection: 'column' }}>
      {items.map((item) => (
        <Row key={item.id} item={item} now={now} />
      ))}
    </div>
  );
}

function Row({ item, now }: { item: FeedItem; now: number }) {
  const deviceIcon = pickDeviceIcon(item.deviceType);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '24px 1fr auto',
        alignItems: 'center',
        gap: 12,
        padding: '10px 14px',
        borderRadius: 10,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 24,
          height: 24,
          borderRadius: 7,
          background: T.blueLight,
          color: T.blue,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {deviceIcon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          title={item.linkName}
          style={{
            fontSize: 13,
            color: T.navy,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {item.event} ·{' '}
          <span style={{ color: T.textMuted, fontWeight: 500 }}>
            {item.linkName}
          </span>
        </div>
        <div style={{ fontSize: 11, color: T.textMuted }}>
          {item.country ? `${item.country.flag} ${item.country.code}` : '—'}
        </div>
      </div>
      <time
        dateTime={item.timestampIso}
        style={{
          fontSize: 11,
          color: T.textMuted,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatRelativeTime(item.timestampIso, now)}
      </time>
    </div>
  );
}

function pickDeviceIcon(kind: FeedDeviceType): React.ReactNode {
  // Inline minimal SVGs — match the existing Icon shapes in Dashboard.tsx.
  const common = {
    width: 13,
    height: 13,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: T.blue,
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  if (kind === 'mobile') {
    return (
      <svg {...common}>
        <rect x="5" y="2" width="14" height="20" rx="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>
    );
  }
  if (kind === 'tablet') {
    return (
      <svg {...common}>
        <rect x="4" y="3" width="16" height="18" rx="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>
    );
  }
  // desktop + other → globe (matches the legacy mock)
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

// ─── Skeleton ──────────────────────────────────────────────────────────────

function SkeletonRows() {
  const { t } = useTranslation();
  const rows = useMemo(() => Array.from({ length: 6 }), []);
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={t('feed.loadingLabel')}
      style={{ padding: '6px 10px 12px', display: 'flex', flexDirection: 'column' }}
    >
      {rows.map((_, i) => (
        <div
          key={i}
          style={{
            display: 'grid',
            gridTemplateColumns: '24px 1fr auto',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
          }}
        >
          <div style={{ width: 24, height: 24, borderRadius: 7, background: T.bgTint }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ height: 11, width: 160, borderRadius: 4, background: T.muted }} />
            <div style={{ height: 9, width: 60, borderRadius: 4, background: T.bgTint }} />
          </div>
          <div style={{ height: 10, width: 48, borderRadius: 4, background: T.bgTint }} />
        </div>
      ))}
    </div>
  );
}

// ─── Empty states ──────────────────────────────────────────────────────────

function ZeroState() {
  const { t } = useTranslation();
  return (
    <div
      style={{
        padding: '28px 24px 24px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        alignItems: 'center',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: T.navy }}>
        {t('feed.zeroTitle')}
      </div>
      <div style={{ fontSize: 12, color: T.textMuted, maxWidth: 280 }}>
        {t('feed.zeroBody')}
      </div>
      <Link
        to={QR_APP_HREF}
        style={{
          marginTop: 6,
          fontSize: 12,
          color: T.purple,
          fontWeight: 600,
          textDecoration: 'none',
        }}
      >
        {t('feed.openQrApp')}
      </Link>
    </div>
  );
}

function FreeUpgradeEmpty() {
  const { t } = useTranslation();
  return (
    <div
      style={{
        padding: '24px 24px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        alignItems: 'flex-start',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: T.navy }}>
        {t('feed.upgradeTitle')}
      </div>
      <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>
        {t('feed.upgradeBody')}
      </div>
      <Link
        to={PRICING_HREF}
        style={{
          marginTop: 4,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          borderRadius: 999,
          background: T.orangeLight,
          color: T.orange,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.04em',
          textDecoration: 'none',
        }}
      >
        {t('feed.upgradeCta')}
      </Link>
    </div>
  );
}

// ─── Retry chip ────────────────────────────────────────────────────────────

function RetryChip({
  onRetry,
  errorMessage,
}: {
  onRetry: () => void;
  errorMessage: string;
}) {
  const { t } = useTranslation();
  return (
    <div
      aria-live="polite"
      style={{
        margin: '6px 14px 12px',
        padding: '8px 12px',
        borderRadius: 10,
        background: T.bgTint,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <span style={{ fontSize: 11, color: T.textMuted }} title={errorMessage}>
        {t('feed.retryMessage')}
      </span>
      <button
        type="button"
        onClick={onRetry}
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: T.purple,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        {t('feed.retryButton')}
      </button>
    </div>
  );
}
